import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';

export function useStorePageGuard(options?: { requireAdmin?: boolean; requireManager?: boolean }) {
  const { selectedStore, isLoadingStores, userStores, selectStore } = useStore();
  const { currentUser, isLoading: isAuthLoading } = useAuth();
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const hasLoadedStores = useRef(false);
  useEffect(() => {
    if (!isLoadingStores) {
      hasLoadedStores.current = true;
    }
  }, [isLoadingStores]);

  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';
  const isManager = currentUser?.role?.role_name?.toLowerCase() === 'manager';
  const canAccess =
    (!options?.requireAdmin && !options?.requireManager) ||
    (options?.requireAdmin && isAdmin) ||
    (options?.requireManager && (isAdmin || isManager));

  useEffect(() => {
    if (isAuthLoading || isLoadingStores) return;
    if (!currentUser) {
      navigate('/login', { replace: true });
      return;
    }
    if (!storeId) {
      navigate('/select-store', { replace: true });
      return;
    }
    if (!canAccess) {
      if (options?.requireAdmin) {
        navigate('/select-store', { replace: true });
      } else if (options?.requireManager) {
        navigate('/select-store', { replace: true });
      }
      return;
    }
    if (hasLoadedStores.current && userStores.length === 0) {
      return;
    }
    const numStoreId = parseInt(storeId, 10);
    const found = userStores.find(s => s.id === numStoreId);
    if (!found) {
      navigate('/select-store', { replace: true });
      return;
    }
    if (!selectedStore || selectedStore.id !== numStoreId) {
      selectStore(found);
    }
  }, [isAuthLoading, isLoadingStores, storeId, selectedStore, userStores, selectStore, navigate, canAccess, currentUser, options?.requireAdmin, options?.requireManager]);

  // Loading state: true if either context is loading, or store is not yet selected
  const loading = isAuthLoading || isLoadingStores || !storeId || !selectedStore || selectedStore.id !== parseInt(storeId, 10);

  // Error state: only for role-based access
  let error: string | null = null;
  if (!canAccess && !loading) {
    if (options?.requireAdmin) error = 'Access denied. Only administrators can access this page.';
    else if (options?.requireManager) error = 'Access denied. Only managers or administrators can access this page.';
    else error = 'Access denied.';
  }

  return { loading, store: selectedStore, error };
} 